import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SommelierPublicDto } from '@proxy/catalogs';
import { CatalogService } from '@proxy/catalogs/catalog.service';
import { WineDto } from '@proxy/wines/models';
import { WineryDto } from '@proxy/wineries/models';
import { SelectOption } from '../shared/components/metro-form-select/metro-form-select.component';
import { finalize, map, startWith, timeout } from 'rxjs/operators';
import { AuthService, LocalizationService } from '@abp/ng.core';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { CountryDto } from '@proxy/countries';
import { APP_ICONS } from '../shared/icons/app-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SelectedSommelierService } from '../shared/services/selected-sommelier.service';
import { ImagePreloadService } from '../shared/services/image-preload.service';
import { StorageService } from '../shared/services/storage.service';
import { Location } from '@angular/common';
import { CustomProfileService } from '@proxy';
import { PickUpService } from '@proxy/pick-ups/pick-up.service';
import { CustomUpdateProfileDto } from '@proxy/profiles/dto';
import { WINE_SORT_CHOICES, WineSortValue } from '../shared/constants/wine-sort-options';
import { SeoService } from '../shared/services/seo.service';
@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.component.html',
  standalone: false,
})
export class CatalogComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService = inject(AuthService);
  safeWaIcon: SafeHtml;
  slug = '';
  @ViewChild('infiniteScrollTrigger') infiniteScrollTrigger?: ElementRef<HTMLElement>;
  private intersectionObserver?: IntersectionObserver;
  private observerAttached = false;  // ← evita reconectar en cada cycle

  sommelier: SommelierPublicDto | null = null;
  loading = true;
  loadingMore = false;
  wines: WineDto[] = [];
  wineries: WineryDto[] = [];
  totalCount = 0;
  currentPage = 0;
  pageSize = 12;
  readonly skeletonCards = Array.from({ length: this.pageSize });
  hasMoreData = true;
  whatsAppHref: string | null = null;

  selectedCountry: string | null = null;
  selectedRegion: string | null = null;
  selectedWinery: number | null = null;

  // winepassport#362 (WP-016) — buscador por nombre, bodega y varietal.
  @ViewChild('searchBox') searchBox?: ElementRef<HTMLElement>;
  keyword = '';
  showMatches = false;
  suggestions: WineDto[] = [];
  loadingSuggestions = false;
  // Error al cargar el listado: se muestra con "Reintentar" en vez de "no encontramos vinos".
  loadError = false;
  // Cada búsqueda nueva invalida las respuestas en vuelo de la anterior (tipeo rápido).
  private requestSeq = 0;
  private institutionalSlug?: string;
  private seoSub?: Subscription;

  countries: CountryDto[] = [];
  countryOptions: SelectOption[] = [];

  regionOptions: SelectOption[] = [];

  wineryOptions: SelectOption[] = [];
  isDesktop$!: Observable<boolean>;

  pickupName: string | null = null;
  selectedPickup: number | null = null;
  pickupOptions: SelectOption[] = [];
  showPickupSelect: boolean = false;
  updatingPickup: boolean = false;
  userProfile: CustomUpdateProfileDto | null = null;

  // winepassport#363 (WP-017) — orden del catálogo, persistido en el query param `sort` de la
  // URL para sobrevivir a la navegación ficha→volver (ver acceptance criteria del ticket).
  selectedSort: WineSortValue | null = null;
  sortOptions: SelectOption[] = [];
  // winepassport#398 — selector de orden en formato texto (mobile)
  sortMenuOpen = false;

  // Sin ?sort= el backend usa su orden por defecto: se muestra como "Predeterminado" en vez de dejar
  // "Ordenar por" sin opción. Texto del orden por defecto (Catalog:Sort:Default). Si el API todavía no lo tiene, ABP devuelve la clave
  // cruda: en ese caso no se muestra (ni en el encabezado ni como opción del menú).
  get defaultSortLabel(): string {
    const text = this.localization.instant('::Catalog:Sort:Default');
    return text.includes('Catalog:Sort:Default') ? '' : text;
  }

  get selectedSortLabel(): string {
    return this.sortOptions.find(o => o.value === this.selectedSort)?.label || this.defaultSortLabel;
  }

  pickSort(value: string): void {
    this.sortMenuOpen = false;
    this.onSortChange(value);
  }

    iconBack = APP_ICONS.arrow;


  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private catalogService: CatalogService,
    private slugService: SelectedSommelierService,
    private imagePreloadService: ImagePreloadService,
    private storageService: StorageService,
    private location: Location,
    private profileService: CustomProfileService,
    public router: Router,
    private localization: LocalizationService,
    private seoService: SeoService,

  ) {
   this.safeWaIcon = this.sanitizer.bypassSecurityTrustHtml(APP_ICONS.whatsapp);

  }

  ngOnInit(): void {
    this.isDesktop$ = fromEvent(window, 'resize').pipe(
      map(() => window.innerWidth >= 768),
      startWith(window.innerWidth >= 768)
    );

    this.route.paramMap.subscribe(() => this.applyCatalogSeo());

    this.slug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (!this.slug) {
      this.loading = false; return; }

    this.slugService.setSlug(this.slug);

    // winepassport#363 (WP-017) — orden elegido, persistido en la URL (?sort=...).
    this.sortOptions = WINE_SORT_CHOICES.map(c => ({
      value: c.value,
      label: this.localization.instant(c.labelKey),
    }));
    const sortFromUrl = this.route.snapshot.queryParamMap.get('sort');
    if (WINE_SORT_CHOICES.some(c => c.value === sortFromUrl)) {
      this.selectedSort = sortFromUrl as WineSortValue;
    }

    this.catalogService.getBySlug(this.slug).subscribe({
      next: r => {

        if (!r) {
          this.router.navigate(['/']);
          return;
        }

        this.sommelier = r;
        this.loading = false;
        this.loadInitialWines();
        this.loadCountries();
        this.whatsAppHref = this.buildWhatsAppLink(r?.phone, this.getDefaultWaMessage());

      },
      error: () => {
        this.router.navigate(['/']);
      }
    });

  }

  // Catálogo personal -> canonical al institucional + noindex; institucional -> index.
  // Ante error/timeout no se deja ninguna señal (nunca una vieja de otra ruta).
  private applyCatalogSeo(): void {
    this.seoSub?.unsubscribe();
    const currentSlug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (this.institutionalSlug) {
      this.applySeoSignals(currentSlug, this.institutionalSlug);
      return;
    }
    this.clearSeoSignals();
    this.seoSub = this.catalogService.getDefaultSommelier({ skipHandleError: true })
      .pipe(timeout(3000))
      .subscribe({
        next: result => {
          if (!result?.slug) return;
          this.institutionalSlug = result.slug;
          this.applySeoSignals(currentSlug, result.slug);
        },
        error: () => this.clearSeoSignals(),
      });
  }

  private applySeoSignals(currentSlug: string, institutionalSlug: string): void {
    this.seoService.setCanonical(`https://winepassport.com.ar/${institutionalSlug}/catalog`);
    this.seoService.setRobots(currentSlug === institutionalSlug ? 'index,follow' : 'noindex,follow');
  }

  private clearSeoSignals(): void {
    this.seoService.removeCanonical();
    this.seoService.removeRobots();
  }

  loadWines(): void {
    if (this.loadingMore || !this.hasMoreData) return;

    this.loadingMore = true;
    const seq = this.requestSeq;
    this.catalogService.getWinesPaged({
      keyword: this.activeKeyword,
      country: this.selectedCountry || undefined,
      wineryId: this.selectedWinery || undefined,
      region: this.selectedRegion || undefined,
      skipCount: this.currentPage * this.pageSize,
      maxResultCount: this.pageSize,
      sommelierSlug: this.slug,
      sorting: this.selectedSort || undefined
    }).pipe(finalize(() => { if (seq === this.requestSeq) this.loadingMore = false; }))
      .subscribe({
        next: result => {
          if (seq !== this.requestSeq) return;
          if (result.items.length === 0) {
            this.hasMoreData = false;
            return;
          }

          this.wines = [...this.wines, ...result.items];

          this.totalCount = result.totalCount;
          this.currentPage++;
          this.hasMoreData = this.wines.length < this.totalCount;
        },
        error: () => { if (seq === this.requestSeq) { this.loadingMore = false; this.loadError = true; } }
      });
  }

  loadWineries(): void {
    this.catalogService.getWineriesByCountryAndRegion(this.selectedCountry, this.selectedRegion).subscribe({
      next: result => {
        this.wineries = result;
        this.wineryOptions = result.map(winery => ({
          value: winery.id,
          label: winery.name || ''
        }));
      }
    });
  }

  loadCountries() {
    this.catalogService.getCountries().subscribe(countries => {
      this.countries = countries;
      this.countryOptions = countries.map(c => ({
        value: c.name,
        label: c.name
      }));
    });
  }

 onCountryChange(value: string): void {
  this.selectedCountry = value;

  const country = this.countries.find(c => c.name === value);
  this.regionOptions = country?.regions.map(r => ({
    value: r,
    label: r
  })) || [];

  this.selectedRegion = null;
  this.selectedWinery = null;

  this.resetWines();
}

  loadInitialWines(): void {
    if (this.loadingMore || !this.hasMoreData) return;
    this.loadingMore = true;
    const seq = this.requestSeq;

    this.catalogService.getWinesPaged({
      keyword: this.activeKeyword,
      country: this.selectedCountry || undefined,
      wineryId: this.selectedWinery || undefined,
      region: this.selectedRegion || undefined,
      skipCount: this.currentPage * this.pageSize,
      maxResultCount: this.pageSize,
      sommelierSlug: this.slug,
      sorting: this.selectedSort || undefined
    }).pipe(finalize(() => { if (seq === this.requestSeq) this.loadingMore = false; }))
      .subscribe({
        next: result => {
          if (seq !== this.requestSeq) return;
          if (result.items.length === 0) {
            this.hasMoreData = false;
            // winepassport#362 — sin resultados para lo buscado: "Te pueden interesar".
            if (this.activeKeyword) this.loadSuggestions(seq);
            return;
          }
          this.wines = [...this.wines, ...result.items];

          this.totalCount = result.totalCount;
          this.currentPage++;
          this.hasMoreData = this.wines.length < this.totalCount;

          if (this.wines.length > 0) {
            // Conectar el observer la primera vez que haya vinos en el DOM
            setTimeout(() => {
              this.setupIntersectionObserver();
              this.fillPageIfNeeded();
            }, 300);
          }
        },
        error: () => { if (seq === this.requestSeq) { this.loadingMore = false; this.loadError = true; } }
      });
  }

  fillPageIfNeeded(): void {
    // Evitar llamadas si ya se está cargando
    if (this.loadingMore || !this.hasMoreData) {
      return;
    }

    const windowHeight = window.innerHeight;
    const documentHeight = Math.max(
      document.body.scrollHeight, document.body.offsetHeight,
      document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight
    );

    // Solo cargar más si realmente es necesario (página muy corta)
    if (documentHeight <= windowHeight + 100 && this.hasMoreData) {
      this.loadWines();
    }
  }

onRegionChange(value: string): void {
  this.selectedRegion = value || null;

  // Siempre limpiar bodega al cambiar región
  this.selectedWinery = null;
  this.wineryOptions = [];
  this.wineries = [];

  // Recargar vinos con el nuevo filtro de región
  this.resetWines();

  // Cargar bodegas si hay país y región seleccionados
  if (this.selectedCountry && this.selectedRegion) {
    this.loadWineries();
  }
}


  onWineryChange(value: number): void {
    this.selectedWinery = value || null;
    this.resetWines();
  }

  // winepassport#363 (WP-017) — persistido en la URL para sobrevivir a ficha→volver.
  onSortChange(value: string): void {
    this.selectedSort = (value || null) as WineSortValue | null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort: this.selectedSort || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.resetWines();
  }

  resetWines(): void {
    this.requestSeq++;
    this.loadingMore = false;
    this.suggestions = [];
    this.loadingSuggestions = false;
    this.loadError = false;
    this.wines = [];
    this.currentPage = 0;
    this.hasMoreData = true;
    // Al resetear (cambio de filtro) desconectamos el observer para reconectarlo
    // con el nuevo set de vinos
    this.observerAttached = false;
    this.intersectionObserver?.disconnect();
    this.loadInitialWines();
  }

  /** Palabra clave que se manda al back: desde 3 letras o números (signos y espacios no cuentan, igual que en el back). */
  get activeKeyword(): string | undefined {
    const k = this.keyword.trim();
    return (k.match(/[\p{L}\p{N}]/gu) || []).length >= 3 ? k : undefined;
  }

  retryLoad(): void {
    if (this.wines.length === 0) {
      this.resetWines();
    } else {
      this.loadError = false;
      this.loadWines();
    }
  }

  /** Coincidencias debajo del buscador mientras se escribe (primeros 5 resultados). */
  get matchPreview(): WineDto[] {
    return this.showMatches && this.activeKeyword ? this.wines.slice(0, 5) : [];
  }

  onSearch(value: string): void {
    const previous = this.activeKeyword;
    this.keyword = value ?? '';
    this.showMatches = !!this.activeKeyword;
    // Con 1 o 2 letras no se filtra: solo se recarga si antes había una búsqueda activa.
    if (this.activeKeyword !== previous) this.resetWines();
  }

  onClearSearch(): void {
    const hadKeyword = !!this.activeKeyword;
    this.keyword = '';
    this.showMatches = false;
    if (hadKeyword) this.resetWines();
  }

  openWine(wine: WineDto): void {
    this.showMatches = false;
    this.slugService.navigate(['catalog', 'wine', wine.id.toString()]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.sortMenuOpen && !(event.target as HTMLElement).closest('[data-sort-menu]')) {
      this.sortMenuOpen = false;
    }
    if (this.showMatches && this.searchBox && !this.searchBox.nativeElement.contains(event.target as Node)) {
      this.showMatches = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.sortMenuOpen = false;
    this.showMatches = false;
  }

  private loadSuggestions(seq: number): void {
    this.loadingSuggestions = true;
    this.catalogService.getWineSuggestions({
      keyword: this.activeKeyword,
      country: this.selectedCountry || undefined,
      sommelierSlug: this.slug,
      maxResultCount: 8,
    })
      .pipe(finalize(() => { if (seq === this.requestSeq) this.loadingSuggestions = false; }))
      .subscribe({
        next: items => { if (seq === this.requestSeq) this.suggestions = items ?? []; },
        // Sin sugerencias queda solo el mensaje; el error se deja en consola para diagnóstico.
        error: err => console.warn('[Catalog] No se pudieron cargar las sugerencias', err),
      });
  }

  getWineDisplayData(wine: WineDto) {
    // Si tiene maxOrderQuantity definido, usarlo; si no, usar el stock o un valor por defecto
    const maxOrder = wine.maxOrderQuantity
      ? Math.min(wine.stock, wine.maxOrderQuantity)
      : Math.min(wine.stock, 24);

    return {
      name: wine.name || '',
      year: wine.harvest || '',
      stock: wine.stock || 0,
      maxOrder: maxOrder,
      location: wine.wineryCountry || '',
      price: wine.price,
      imageUrl: wine.imageUrl
    };
  }

  get hasLoggedIn(): boolean {
    return this.authService.isAuthenticated;
  }

  goBack(): void {
    this.location.back();
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.seoSub?.unsubscribe();
    this.clearSeoSignals();
  }
  private getDefaultWaMessage(): string {
    return '¡Hola! ¿Cómo estás? Te escribo desde WinePassport y me interesó mucho tu catálogo.\n\n¿Podemos coordinar y confirmar disponibilidad? ¡Gracias!';
  }

  private buildWhatsAppLink(raw?: string | null, message?: string | null): string | null {
    if (!raw) return null;

    let p = raw.startsWith('+')
      ? raw.slice(1)                          // E.164 guardado: solo quitar '+'
      : raw.replace(/\D+/g, '');             // fallback: solo dígitos

    if (p.startsWith('00')) p = p.slice(2);

    const base = `https://wa.me/${p}`;
    const txt = message ? encodeURIComponent(message) : '';
    return txt ? `${base}?text=${txt}` : base;
  }

  ngAfterViewInit(): void {
    // NO llamamos setupIntersectionObserver() aquí porque el trigger
    // (#infiniteScrollTrigger) todavía no existe en el DOM (los vinos
    // se cargan de forma asíncrona). Lo conectamos luego de la primera carga.
    console.log('[Catalog] ngAfterViewInit — trigger existe?', !!this.infiniteScrollTrigger);
  }

  private setupIntersectionObserver(): void {
    // Evitar reconectar si ya está activo
    if (this.observerAttached) {
      console.log('[Catalog] Observer ya estaba conectado, skip.');
      return;
    }

    if (!this.infiniteScrollTrigger) {
      // El elemento todavía no está en el DOM — se reintentará tras la próxima carga
      console.warn('[Catalog] infiniteScrollTrigger NO existe en el DOM todavía — observer NO conectado');
      return;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loadingMore && this.hasMoreData) {
          console.log('[Catalog] 🔽 Trigger visible — cargando página', this.currentPage + 1);
          this.loadWines();
        }
      });
    }, { root: null, rootMargin: '800px 0px 400px 0px', threshold: 0 });

    this.intersectionObserver.observe(this.infiniteScrollTrigger.nativeElement);
    this.observerAttached = true;
    console.log('[Catalog] ✅ IntersectionObserver conectado al trigger. Vinos en pantalla:', this.wines.length);
  }


}
